import { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
//
import { getBannerList } from '../../../api/services/systemService';
import { useSelector } from 'react-redux';
import { isTokenExpired, navigateToLogin } from '../../../utils';

// 애니메이션 정의
const fadeIn = keyframes`
  from { opacity: 0.5; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0.5; transform: translateX(-20px); }
`;

// 컨테이너 스타일
const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    width: 100%;
    margin: 0;
  }
`;

// 슬라이드 영역 스타일
const Slide = styled.div`
  display: ${(props) => (props.active ? 'block' : 'none')};
  width: 100%;
  animation: ${(props) => (props.active ? fadeIn : fadeOut)} 0.8s ease-in-out;
  cursor: pointer;
`;

// 이미지 스타일
const Image = styled.img`
  width: 100%;
  height: auto;
  min-height: 200px;
  display: block;
  object-fit: cover;

  @media (max-width: 768px) {
    min-height: 180px;
  }

  @media (max-width: 480px) {
    min-height: 120px;
  }
`;

// 화살표 버튼 스타일
const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  color: ${(props) => props.theme.colors.primaryText};
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  z-index: 10;
  border-radius: 50%;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-50%) scale(1.1);
    background: rgba(255, 255, 255, 0.9);
    color: #000;
  }

  ${(props) => (props.left ? 'left: 20px;' : 'right: 20px;')}

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 14px;
    ${(props) => (props.left ? 'left: 15px;' : 'right: 15px;')}
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    ${(props) => (props.left ? 'left: 10px;' : 'right: 10px;')}
  }
`;

// 하단 점(인디케이터) 컨테이너
const DotsContainer = styled.div`
  position: absolute;
  width: 100%;
  bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    bottom: 15px;
  }

  @media (max-width: 480px) {
    bottom: 10px;
  }
`;

// 개별 점 스타일
const Dot = styled.div`
  width: 10px;
  height: 10px;
  margin: 0 6px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${(props) =>
    props.active &&
    `
      background-color: #000;
      transform: scale(1.2);
    `}

  @media (max-width: 768px) {
    width: 8px;
    height: 8px;
    margin: 0 4px;
  }

  @media (max-width: 480px) {
    width: 6px;
    height: 6px;
    margin: 0 3px;
  }
`;

// 슬라이더 컴포넌트
const BannerSliderView = ({ style }) => {
  const userInfo = useSelector((state) => state.userInfo);
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [bannerList, setBannerList] = useState([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    getDbBannerList();
  }, []);

  // Auto-play interval: change images every 5 seconds
  useEffect(() => {
    if (bannerList.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrent((prev) => (prev === bannerList.length - 1 ? 0 : prev + 1));
      }, 5000); // 5 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [bannerList.length]);

  const getDbBannerList = async () => {
    const result = await getBannerList(userInfo.brandCode);
    setBannerList(result.bannerList);
  };

  const nextSlide = () => {
    setCurrent((prev) => (prev === bannerList.length - 1 ? 0 : prev + 1));
    // Reset interval when manually navigating
    resetInterval();
  };

  const prevSlide = () => {
    setCurrent((prev) => (prev === 0 ? bannerList.length - 1 : prev - 1));
    // Reset interval when manually navigating
    resetInterval();
  };

  const goToSlide = (index) => {
    setCurrent(index);
    // Reset interval when manually navigating
    resetInterval();
  };

  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (bannerList.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrent((prev) => (prev === bannerList.length - 1 ? 0 : prev + 1));
      }, 5000);
    }
  };

  const handleBannerClick = (url) => {
    const token = localStorage.getItem('token');

    // Check if user is logged in
    if (!token || isTokenExpired(token)) {
      // Redirect to login page if not logged in
      navigateToLogin(navigate, url);
    } else {
      // Navigate to banner URL if logged in
      // Check if it's an external URL or internal route
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    }
  };

  return (
    <SliderContainer style={style}>
      {bannerList.map((item, index) => (
        <Slide
          key={index}
          active={index === current}
          onClick={() => handleBannerClick(item.url)}
        >
          <div>
            <Image
              src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${item.filepath1}`}
              alt={`Slide ${index + 1}`}
            />
          </div>
        </Slide>
      ))}
      <Arrow left onClick={prevSlide}>
        &#10094;
      </Arrow>
      <Arrow onClick={nextSlide}>&#10095;</Arrow>
      <DotsContainer>
        {bannerList.map((_, idx) => (
          <Dot
            key={idx}
            active={idx === current}
            onClick={() => goToSlide(idx)}
          />
        ))}
      </DotsContainer>
    </SliderContainer>
  );
};

export default BannerSliderView;
